import React from 'react';
import { resolveMediaUrl } from './mediaUrl';

// Minimal inline markdown: ![alt](url) images, **bold**, *italic*.
// Images are matched first so bang-syntax wins over bold; bold before italic
// so ** isn't eaten by the single-* italic rule.
const IMAGE_RE = /!\[([^\]\n]*)\]\(([^)\s]+)\)/;
const TOKEN_RE = /\*\*([^*\n]+?)\*\*|\*([^*\n]+?)\*/g;

function renderInline(line, keyPrefix) {
  const nodes = [];
  let key = 0;
  let lastIndex = 0;
  let match;
  TOKEN_RE.lastIndex = 0;
  while ((match = TOKEN_RE.exec(line)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(line.slice(lastIndex, match.index));
    }
    if (match[1] !== undefined) {
      nodes.push(<strong key={`${keyPrefix}-b-${key++}`}>{match[1]}</strong>);
    } else if (match[2] !== undefined) {
      nodes.push(<em key={`${keyPrefix}-i-${key++}`}>{match[2]}</em>);
    }
    lastIndex = TOKEN_RE.lastIndex;
  }
  if (lastIndex < line.length) {
    nodes.push(line.slice(lastIndex));
  }
  return nodes;
}

// List lines: "- item" / "• item" (bulleted) and "1. item" / "1) item" (numbered).
// A space after the marker is required so "*italic*" lines aren't read as bullets.
const BULLET_RE = /^\s*[-*•]\s+(.*)$/;
const ORDERED_RE = /^\s*(\d+)[.)]\s+(.*)$/;

export function renderTeacherNotes(text) {
  if (!text) return null;
  const nodes = [];
  let key = 0;
  const lines = String(text).split('\n');
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const ordered = line.match(ORDERED_RE);
    const bullet = !ordered && line.match(BULLET_RE);
    if (ordered || bullet) {
      // Collect consecutive lines of the same list kind into one <ul>/<ol>.
      const re = ordered ? ORDERED_RE : BULLET_RE;
      const items = [];
      const startNum = ordered ? parseInt(ordered[1], 10) : undefined;
      while (i < lines.length) {
        const m = lines[i].match(re);
        if (!m) break;
        items.push(
          <li key={`li-${i}`}>{renderInline(ordered ? m[2] : m[1], `l${i}`)}</li>
        );
        i++;
      }
      nodes.push(ordered ? (
        <ol key={`ol-${key++}`} start={startNum !== 1 ? startNum : undefined} className="list-decimal pl-5 my-1 whitespace-normal">
          {items}
        </ol>
      ) : (
        <ul key={`ul-${key++}`} className="list-disc pl-5 my-1 whitespace-normal">
          {items}
        </ul>
      ));
      continue;
    }

    const img = line.match(IMAGE_RE);
    if (img && img[0] === line.trim()) {
      const [, alt, src] = img;
      nodes.push(
        <img
          key={`img-${key++}`}
          src={resolveMediaUrl(src)}
          alt={alt || ''}
          className="my-2 max-w-full h-auto rounded-md border border-amber-200"
        />
      );
    } else {
      nodes.push(...renderInline(line, `l${i}`));
    }
    // Lists are block-level, so skip the <br> right before one.
    const nextIsList = i + 1 < lines.length && (ORDERED_RE.test(lines[i + 1]) || BULLET_RE.test(lines[i + 1]));
    if (i < lines.length - 1 && !nextIsList) {
      nodes.push(<br key={`br-${key++}`} />);
    }
    i++;
  }
  return nodes;
}
